import React, { useState, useRef, useEffect } from 'react';
import { aiAssistantService } from '@/api/aiAssistant';
import { isCompanyUser } from '@/utils/roleStorage';
import { useQuery } from "@tanstack/react-query";
import { reportsService } from "@/api/reports";
import {TaleDashbaordMainCard} from "@components/TaleDashbaordMainCard";

const BIAnalysisPage = () => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(null);

    const isCompany = isCompanyUser();
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Fetch real survey data
    const { data: overviewData, isPending: isOverviewPending } = useQuery({
        queryKey: ["surveyOverview"],
        queryFn: async () => await reportsService.getSurveyOverview(),
        enabled: isCompany,
    });

    // Theme classes
    const businessClasses = {
        gradientPrimary: 'bg-tale-business',
        gradientLight: 'bg-tale-business-light',
        bgPrimary: 'bg-tale-business',
        textPrimary: 'text-tale-business',
        hoverPrimary: 'hover:bg-tale-business',
        ringPrimary: 'focus:ring-tale-business',
        borderPrimary: 'border-tale-business',
    };

    const normalClasses = {
        gradientPrimary: 'bg-tale-normal',
        gradientLight: 'bg-tale-normal-light',
        bgPrimary: 'bg-tale-normal',
        textPrimary: 'text-tale-normal',
        hoverPrimary: 'hover:bg-tale-normal',
        ringPrimary: 'focus:ring-tale-normal',
        borderPrimary: 'border-tale-normal',
    };

    const classes = isCompany ? businessClasses : normalClasses;

    // Load BI conversation history on mount
    useEffect(() => {
        loadBIConversationHistory();
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const loadBIConversationHistory = async () => {
        try {
            const history = await aiAssistantService.getHistory();
            const formattedMessages = history.reverse().flatMap(item => [
                { type: 'user', content: item.userQuestion, timestamp: item.createdAt },
                { type: 'assistant', content: item.assistantResponse, timestamp: item.createdAt }
            ]);
            setMessages(formattedMessages);

            if (formattedMessages.length === 0) {
                const welcomeMessage = {
                    type: 'assistant',
                    content: "Welcome to BI Analysis Assistant! I'm here to help you analyze your survey data. Ask me about trends, insights, reports, or request data analysis.",
                    timestamp: new Date().toISOString()
                };
                setMessages([welcomeMessage]);
            }
        } catch (error) {
            console.error('Error loading BI history:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = inputMessage.trim();
        const userMessageObj = {
            type: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessageObj]);
        setInputMessage('');
        setIsLoading(true);

        try {
            const response = await aiAssistantService.askQuestion(userMessage, {
                context: 'BI_ANALYSIS',
                category: 'business_intelligence'
            });

            if (response.conversationId && conversationId !== response.conversationId) {
                setConversationId(response.conversationId);
            }

            const aiMessageObj = {
                type: 'assistant',
                content: response.answer,
                timestamp: response.timestamp
            };

            setMessages(prev => [...prev, aiMessageObj]);

        } catch (error) {
            console.error('Error sending message:', error);

            const errorMessageObj = {
                type: 'assistant',
                content: "Sorry, I encountered an error while processing your request. Please try again.",
                timestamp: new Date().toISOString()
            };

            setMessages(prev => [...prev, errorMessageObj]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleResetContext = async () => {
        try {
            await aiAssistantService.resetContext();
            setMessages([]);
            setConversationId(null);

            const welcomeMessage = {
                type: 'assistant',
                content: "BI Analysis context reset. I'm ready to help with your business intelligence queries!",
                timestamp: new Date().toISOString()
            };
            setMessages([welcomeMessage]);
        } catch (error) {
            console.error('Error resetting BI context:', error);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // BI-specific example questions
    const biExampleQuestions = [
        "Show me survey response trends",
        "Analyze survey completion rates",
        "Generate a survey insights report",
        "What are the most common survey responses?",
        "Analyze response patterns by question",
        "Compare survey performance metrics"
    ];

    if (!isCompany) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center p-8">
                    <div className="text-red-500 text-2xl font-bold mb-4">Access Restricted</div>
                    <p className="text-gray-600">BI Analysis features are only available for company users.</p>
                </div>
            </div>
        );
    }

    return (
        <TaleDashbaordMainCard>
                {/* Header */}
                <header className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                BI Analysis Dashboard
                            </h1>
                            <p className="text-gray-600">
                                AI-powered survey data analysis and insights
                            </p>
                        </div>
                        <div className="flex space-x-3 mt-4 md:mt-0">
                            <button
                                onClick={handleResetContext}
                                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Reset Chat
                            </button>
                        </div>
                    </div>

                    {/* Real Stats from API */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            <div className="text-sm text-gray-500">Total Surveys</div>
                            <div className="text-2xl font-bold text-gray-800">
                                {isOverviewPending ? "..." : overviewData?.totalCreatedSurveys || 0}
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            <div className="text-sm text-gray-500">Active Surveys</div>
                            <div className="text-2xl font-bold text-gray-800">
                                {isOverviewPending ? "..." : overviewData?.totalOpenSurveys || 0}
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            <div className="text-sm text-gray-500">Total Responses</div>
                            <div className="text-2xl font-bold text-gray-800">
                                {isOverviewPending ? "..." : overviewData?.totalResponses || 0}
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            <div className="text-sm text-gray-500">Avg. Completion</div>
                            <div className="text-2xl font-bold text-gray-800">78%</div>
                        </div>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Sidebar - Quick Actions */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                🚀 Quick Actions
                            </h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setInputMessage("Show overall survey statistics")}
                                    className="w-full text-left p-3 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-700 transition-colors"
                                >
                                    Show Survey Stats
                                </button>
                                <button
                                    onClick={() => setInputMessage("Analyze response patterns and trends")}
                                    className="w-full text-left p-3 bg-green-50 hover:bg-green-100 rounded-lg text-green-700 transition-colors"
                                >
                                    Analyze Responses
                                </button>
                                <button
                                    onClick={() => setInputMessage("Generate a comprehensive survey report")}
                                    className="w-full text-left p-3 bg-purple-50 hover:bg-purple-100 rounded-lg text-purple-700 transition-colors"
                                >
                                    Generate Report
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                💡 Example Questions
                            </h3>
                            <div className="space-y-3">
                                {biExampleQuestions.map((question, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setInputMessage(question)}
                                        className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-700 text-sm transition-colors"
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Main Chat Area */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden h-[600px] flex flex-col">
                            {/* Chat Header */}
                            <div className={`p-4 bg-gradient-to-r ${classes.gradientLight} border-b`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className={`bg-gradient-to-r ${classes.gradientPrimary} p-2 rounded-full`}>
                                            <span className="text-white font-bold">BI</span>
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-800">BI Analysis Assistant</h2>
                                            <p className="text-sm text-gray-600">Specialized in survey data analysis</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="h-2 w-2 bg-green-500 rounded-full"></span>
                                        <span className="text-xs text-gray-600">Online</span>
                                    </div>
                                </div>
                            </div>

                            {/* Messages Container */}
                            <div className="flex-1 p-4 space-y-4 overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
                                {messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                        <div className={`bg-gradient-to-r ${classes.gradientPrimary} p-6 rounded-full mb-6`}>
                                            <span className="text-white text-2xl font-bold">BI</span>
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                            Start Your BI Analysis
                                        </h3>
                                        <p className="text-gray-600 mb-6">
                                            Ask me to analyze survey data, generate insights, or create reports.
                                        </p>
                                    </div>
                                ) : (
                                    messages.map((message, index) => (
                                        <div key={index} className={`flex ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                            {message.type === 'assistant' && (
                                                <div className="flex-shrink-0 mr-2 mt-1">
                                                    <div className={`w-8 h-8 bg-gradient-to-r ${classes.gradientPrimary} rounded-full flex items-center justify-center`}>
                                                        <span className="text-white text-xs font-bold">BI</span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className={`max-w-xl ${message.type === 'user' ? 'order-1' : 'order-2'}`}>
                                                <div className={`p-3 rounded-2xl ${message.type === 'user'
                                                    ? `bg-gradient-to-br ${classes.gradientPrimary} text-white rounded-br-none`
                                                    : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                                                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                                    <div className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                                                        {formatTime(message.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                            {message.type === 'user' && (
                                                <div className="flex-shrink-0 ml-2 mt-1">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                                                        <span className="text-white text-xs font-bold">ME</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                                {isLoading && (
                                    <div className="flex justify-start">
                                        <div className="flex-shrink-0 mr-2 mt-1">
                                            <div className={`w-8 h-8 bg-gradient-to-r ${classes.gradientPrimary} rounded-full flex items-center justify-center`}>
                                                <span className="text-white text-xs font-bold">BI</span>
                                            </div>
                                        </div>
                                        <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-none shadow">
                                            <div className="flex space-x-1">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-75"></div>
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150"></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Input Area */}
                            <div className="p-4 bg-gray-50 border-t">
                                <div className="flex items-center space-x-3">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="Ask about survey analysis, insights, or reports..."
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={isLoading}
                                        className={`flex-1 bg-white border border-gray-300 rounded-full py-3 px-5 text-sm text-gray-800 placeholder-gray-500 outline-none focus:ring-2 ${classes.ringPrimary} focus:border-transparent disabled:opacity-50`}
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={isLoading || !inputMessage.trim()}
                                        className={`p-3 rounded-full transition-colors duration-200 ${isLoading || !inputMessage.trim()
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : `bg-gradient-to-r ${classes.gradientPrimary} text-white ${classes.hoverPrimary}`
                                        }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transform rotate-45"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                                        </svg>
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
       </TaleDashbaordMainCard>
    );
};

export default BIAnalysisPage;
